import { Col, Row, Segmented, Spin } from "antd";
import { useEffect, useState } from "react";
import BusinessInfo from "./BusinessInfo";
import Locations from "./Locations";
import Account from "./Account";
import Subscription from "./Subscription";
import { useNavigation, useSearchParams } from "react-router-dom";
import { isMobile } from "react-device-detect";
import Domain from "./Domain";
import { useSelector } from "react-redux";
import { DomainState } from "redux-context/domain";
import AIMatch from "./AIMatch";
import "./Settings.scss";

enum SettingsTabs {
  BUSINESS_INFO = "business-info",
  LOCATIONS = "locations",
  AI_MATCH = "ai-match",
  DOMAIN = "domain",
  SUBSCRIPTION = "subscription",
  ACCOUNT = "account",
}

const settingsTabsSet = new Set(Object.values(SettingsTabs));

const parseTabFromQuery = (value: string | null): SettingsTabs => {
  if (!value) return SettingsTabs.BUSINESS_INFO;
  return settingsTabsSet.has(value as SettingsTabs)
    ? (value as SettingsTabs)
    : SettingsTabs.BUSINESS_INFO;
};

const Settings: React.FC = () => {
  const navigation = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const loading = navigation.state === "loading";
  const [selectedTab, setSelectedTab] = useState<SettingsTabs>(() =>
    parseTabFromQuery(searchParams.get("tab")),
  );
  const {
    metadata: { whiteLabel },
  } = useSelector((state: DomainState) => state.domain);

  const handleTabChange = (nextTab: string) => {
    const parsedTab = parseTabFromQuery(nextTab);
    setSelectedTab(parsedTab);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("tab", parsedTab);
    setSearchParams(nextSearchParams, { replace: true });
  };

  useEffect(() => {
    const parsedTab = parseTabFromQuery(searchParams.get("tab"));
    setSelectedTab((previousTab) =>
      previousTab === parsedTab ? previousTab : parsedTab,
    );
  }, [searchParams]);

  if (loading) return <Spin />;

  return (
    <div className="settings" style={{ padding: "10px 0 " }}>
      <Segmented
        options={[
          { label: "Business Info", value: "business-info" },
          { label: "Locations", value: "locations" },
          ...(whiteLabel ? [{ label: "Domain", value: "domain" }] : []),
          { label: "Billing", value: "subscription" },
          { label: "User", value: "account" },
          { label: "PRO Features", value: "ai-match" },
        ]}
        value={selectedTab}
        onChange={handleTabChange}
        style={{
          marginBottom: isMobile ? 12 : 32,
          width: isMobile ? "100%" : "unset",
        }}
        className="segmented-mobile"
      />
      <Row style={{ marginTop: 40 }}>
        <Col xs={24}>
          {selectedTab === SettingsTabs.BUSINESS_INFO && <BusinessInfo />}
          {selectedTab === SettingsTabs.LOCATIONS && <Locations />}
          {selectedTab === SettingsTabs.AI_MATCH && <AIMatch />}
          {selectedTab === SettingsTabs.DOMAIN && <Domain />}
          {selectedTab === SettingsTabs.SUBSCRIPTION && <Subscription />}
          {selectedTab === SettingsTabs.ACCOUNT && <Account />}
        </Col>
      </Row>
    </div>
  );
};

export default Settings;
